import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PaymentTransaction } from '../../../api/types';
import { Button } from '../../../shared/ui/Button';
import { TransactionReceipt } from '../components/TransactionReceipt';
import { usePaymentTransactions, TRANSACTIONS_PAGE_SIZE } from '../hooks/usePaymentTransactions';
import { formatBillingDate, formatTransactionAmount } from '../lib/billingState';
import styles from './TransactionHistoryScreen.module.css';

// PARA-140 (AC-4, T13) — отдельный экран истории платежей (логический роут
// /billing/history в стеке экранов). Показываются ВСЕ статусы, включая
// REFUNDED: backend-фильтрации нет, пользователь видит полную историю.
// Квитанция открывается модалкой поверх списка — экран не покидается, поэтому
// загруженные страницы и позиция скролла сохраняются (AC-5).
//
// Экран — владелец накопления страниц: хук отдаёт ровно одну страницу
// (usePaymentTransactions), а `offset` и склейка загруженного живут здесь.
export function TransactionHistoryScreen(): JSX.Element {
  const { t, i18n } = useTranslation('billing');

  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<PaymentTransaction[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const query = usePaymentTransactions(true, TRANSACTIONS_PAGE_SIZE, offset);
  const page = query.data;

  // Склейка страниц: срез до текущего offset — это ровно ранее загруженные
  // страницы (каждая непоследняя приходит полной, probe limit + 1), а хвост
  // заменяется текущей страницей. Поэтому повторный прогон эффекта на том же
  // offset (рефетч, фокус окна) переписывает свою страницу, а не дописывает
  // её в конец.
  useEffect(() => {
    if (page === undefined) {
      return;
    }
    setItems((previous) => [...previous.slice(0, offset), ...page.transactions]);
  }, [page, offset]);

  const hasNothingLoaded = items.length === 0;

  return (
    <div className={styles.screen} data-testid="transaction-history">
      <h1 className={styles.title}>{t('history.heading')}</h1>

      {query.isPending && hasNothingLoaded && (
        <p className={styles.message}>{t('history.loading')}</p>
      )}
      {query.isError && (
        <>
          <p className={styles.message} data-testid="history-error">
            {t('history.error')}
          </p>
          <Button variant="secondary" fullWidth onClick={() => void query.refetch()}>
            {t('history.retry')}
          </Button>
        </>
      )}
      {/* AC-4d: «платежей нет» — только когда первая страница пришла пустой.
          Условие по items дало бы вспышку этого текста между ответом и
          склейкой страницы. */}
      {offset === 0 && page?.transactions.length === 0 && (
        <p className={styles.message} data-testid="history-empty">
          {t('history.empty')}
        </p>
      )}

      {items.length > 0 && (
        <ul className={styles.list}>
          {items.map((transaction) => (
            <li key={transaction.id}>
              <button
                type="button"
                className={styles.row}
                data-testid={`transaction-row-${transaction.id}`}
                onClick={() => setSelectedId(transaction.id)}
              >
                <span className={styles.rowMain}>
                  <span className={styles.rowPlan}>{t(`plans.${transaction.plan}.name`)}</span>
                  <span className={styles.rowDate}>
                    {formatBillingDate(transaction.occurred_at, i18n.language)}
                  </span>
                </span>
                <span className={styles.rowSide}>
                  <span className={styles.rowAmount}>
                    {formatTransactionAmount(
                      transaction.amount,
                      transaction.currency,
                      i18n.language,
                    )}
                  </span>
                  <span
                    className={styles.rowStatus}
                    data-testid={`transaction-status-${transaction.id}`}
                  >
                    {t(`transactions.status.${transaction.status}`)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* AC-4f: кнопка есть ровно при has_more=true — на последней странице её
          нет вовсе, а не «неактивная». */}
      {page?.has_more === true && (
        <Button
          variant="secondary"
          fullWidth
          disabled={query.isFetching}
          onClick={() => setOffset((previous) => previous + TRANSACTIONS_PAGE_SIZE)}
        >
          {t('history.more')}
        </Button>
      )}

      {selectedId !== null && (
        <TransactionReceipt transactionId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}
